import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Link as LinkIcon, ExternalLink, Trash2, Edit2 } from 'lucide-react';
import '../../styles/atoms/rich-text-editor.css';
import '../../styles/atoms/link-modal.css';
import '../../styles/atoms/link-hover-menu.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  height?: number;
  className?: string;
}

export interface RichTextEditorRef {
  focus: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder = 'Enter text...',
  height = 440,
  className = '',
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdateRef = useRef(false);
  const savedSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedRangeRef = useRef<Range | null>(null);
  const [hoveredLink, setHoveredLink] = useState<HTMLAnchorElement | null>(null);
  const [linkMenuPosition, setLinkMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const linkMenuRef = useRef<HTMLDivElement>(null);
  const editingLinkRef = useRef<HTMLAnchorElement | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      editorRef.current?.focus();
    },
  }));

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) {
      return null;
    }

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    
    const start = preCaretRange.toString().length;
    const end = start + range.toString().length;
    
    return { start, end };
  };

  const restoreSelection = (savedPos: { start: number; end: number } | null) => {
    if (!savedPos || !editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    try {
      let charCount = 0;
      const nodeStack: Node[] = [editorRef.current];
      let node: Node | undefined;
      let foundStart = false;
      let stop = false;
      const range = document.createRange();
      range.setStart(editorRef.current, 0);
      range.collapse(true);

      while (!stop && (node = nodeStack.pop())) {
        if (node.nodeType === Node.TEXT_NODE) {
          const nextCharCount = charCount + (node.textContent?.length || 0);
          if (!foundStart && savedPos.start >= charCount && savedPos.start <= nextCharCount) {
            range.setStart(node, savedPos.start - charCount);
            foundStart = true;
          }
          if (foundStart && savedPos.end >= charCount && savedPos.end <= nextCharCount) {
            range.setEnd(node, savedPos.end - charCount);
            stop = true;
          }
          charCount = nextCharCount;
        } else {
          let i = node.childNodes.length;
          while (i--) {
            nodeStack.push(node.childNodes[i] as Node);
          }
        }
      }

      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  useEffect(() => {
    if (!editorRef.current) return;
    
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    const currentHtml = editorRef.current.innerHTML;
    if (currentHtml === value) return;

    const savedPos = saveSelection();
    const wasFocused = document.activeElement === editorRef.current;

    editorRef.current.innerHTML = value;

    if (wasFocused && savedPos) {
      requestAnimationFrame(() => {
        restoreSelection(savedPos);
        editorRef.current?.focus();
      });
    }
  }, [value]);

  useEffect(() => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    let hoverTimeout: NodeJS.Timeout | null = null;

        const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (link && editor.contains(link)) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
        }
        
        hoverTimeout = setTimeout(() => {
          const rect = link.getBoundingClientRect();
          const editorRect = editor.getBoundingClientRect();
          const scrollTop = editor.scrollTop;
          const scrollLeft = editor.scrollLeft;
          
          const top = rect.top - editorRect.top + scrollTop;
          const left = rect.left - editorRect.left + scrollLeft;
          
          setHoveredLink(link);
          setLinkMenuPosition({
            top: top,
            left: left,
          });
        }, 300);
      } else {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      const relatedTarget = e.relatedTarget as HTMLElement;
      
      if (relatedTarget && linkMenuRef.current?.contains(relatedTarget)) {
        return;
      }
      
      if (link) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        setTimeout(() => {
          if (!linkMenuRef.current?.matches(':hover') && !linkMenuRef.current?.contains(document.activeElement)) {
            setHoveredLink(null);
            setLinkMenuPosition(null);
          }
        }, 100);
      }
    };

    editor.addEventListener('mouseover', handleMouseOver);
    editor.addEventListener('mouseout', handleMouseOut);

    return () => {
      editor.removeEventListener('mouseover', handleMouseOver);
      editor.removeEventListener('mouseout', handleMouseOut);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, []);

  useEffect(() => {
    if (!linkMenuRef.current || !hoveredLink) return;

    const menu = linkMenuRef.current;
    let hideTimeout: NodeJS.Timeout | null = null;

    const handleMouseEnter = (e: MouseEvent) => {
      e.stopPropagation();
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      e.stopPropagation();
      const relatedTarget = e.relatedTarget as HTMLElement;
      
      if (relatedTarget && hoveredLink.contains(relatedTarget)) {
        return;
      }
      
      hideTimeout = setTimeout(() => {
        setHoveredLink(null);
        setLinkMenuPosition(null);
      }, 150);
    };

    menu.addEventListener('mouseenter', handleMouseEnter);
    menu.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      menu.removeEventListener('mouseenter', handleMouseEnter);
      menu.removeEventListener('mouseleave', handleMouseLeave);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hoveredLink]);

  const execCommand = (command: string, value?: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    
    document.execCommand(command, false, value);
    
    editorRef.current?.focus();
    
    try {
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      cleanUpStyles(editorRef.current);
      const html = editorRef.current.innerHTML;
      
      isInternalUpdateRef.current = true;
      onChange(html);
    }
  };

  const cleanUpStyles = (element: HTMLElement) => {
    const allElements = element.querySelectorAll('*');
    allElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.removeProperty('font-family');
        el.style.removeProperty('font-size');
        el.style.removeProperty('color');
        el.style.removeProperty('background-color');
        el.style.removeProperty('line-height');
        el.style.removeProperty('letter-spacing');
        const tagName = el.tagName.toLowerCase();
        if (tagName !== 'p' && tagName !== 'div') {
          el.style.removeProperty('text-align');
        }
        if (tagName !== 'ul' && tagName !== 'ol' && tagName !== 'li' && tagName !== 'p') {
          el.style.removeProperty('margin');
          el.style.removeProperty('padding');
        }
      }
    });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    
    let html = e.clipboardData.getData('text/html');
    const plainText = e.clipboardData.getData('text/plain');
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    
    const range = selection.getRangeAt(0);
    range.deleteContents();
    
    if (html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      cleanUpStyles(tempDiv);
      
      const processedHtml = tempDiv.innerHTML
        .replace(/<br\s*\/?>/gi, '<br>')
        .replace(/<div>/gi, '<p>')
        .replace(/<\/div>/gi, '</p>')
        .replace(/<p><\/p>/gi, '<br>')
        .replace(/<p>\s*<\/p>/gi, '<br>');
      
      const fragment = document.createRange().createContextualFragment(processedHtml);
      range.insertNode(fragment);
    } else if (plainText) {
      const lines = plainText.split(/\r?\n/);
      lines.forEach((line, index) => {
        if (index > 0) {
          const br = document.createElement('br');
          range.insertNode(br);
          range.setStartAfter(br);
        }
        if (line.trim()) {
          const textNode = document.createTextNode(line);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
        }
      });
    }
    
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    
    if (editorRef.current) {
      cleanUpStyles(editorRef.current);
      const finalHtml = editorRef.current.innerHTML;
      onChange(finalHtml);
    }
  };

  const handleBold = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand('bold');
  };

  const handleItalic = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand('italic');
  };

  const handleUnderline = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand('underline');
  };

  const handleBulletList = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand('insertUnorderedList');
  };

  const handleNumberedList = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    execCommand('insertOrderedList');
  };

  const handleLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const linkElement = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as Element).closest('a')
      : (range.commonAncestorContainer.parentElement?.closest('a') || null);

    if (linkElement) {
      execCommand('unlink');
      return;
    }

    const selectedText = selection.toString();
    savedRangeRef.current = range.cloneRange();
    setLinkText(selectedText);
    setLinkUrl('');
    setLinkModalOpen(true);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl.trim()) {
      return;
    }

    const urlWithProtocol = linkUrl.startsWith('http://') || linkUrl.startsWith('https://') 
      ? linkUrl 
      : `https://${linkUrl}`;

    if (editingLinkRef.current) {
      editingLinkRef.current.setAttribute('href', urlWithProtocol);
      if (linkText.trim() && linkText.trim() !== editingLinkRef.current.textContent) {
        editingLinkRef.current.textContent = linkText.trim();
      }
      editingLinkRef.current = null;
      
      if (editorRef.current) {
        cleanUpStyles(editorRef.current);
        const html = editorRef.current.innerHTML;
        isInternalUpdateRef.current = true;
        onChange(html);
      }
    } else if (savedRangeRef.current && editorRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }

      if (linkText.trim() && !savedRangeRef.current.toString().trim()) {
        savedRangeRef.current.deleteContents();
        const textNode = document.createTextNode(linkText.trim());
        savedRangeRef.current.insertNode(textNode);
        savedRangeRef.current.selectNodeContents(textNode);
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRangeRef.current);
        }
      }

      execCommand('createLink', urlWithProtocol);
    }

    setLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
    savedRangeRef.current = null;
  };

  const handleLinkCancel = () => {
    setLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
    savedRangeRef.current = null;
    editingLinkRef.current = null;
  };

  const handleOpenLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hoveredLink) {
      const url = hoveredLink.getAttribute('href');
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
    setHoveredLink(null);
    setLinkMenuPosition(null);
  };

  const handleRemoveLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hoveredLink && editorRef.current) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(hoveredLink);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      execCommand('unlink');
    }
    setHoveredLink(null);
    setLinkMenuPosition(null);
  };

  const handleEditLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hoveredLink) {
      const url = hoveredLink.getAttribute('href') || '';
      const text = hoveredLink.textContent || '';
      
      editingLinkRef.current = hoveredLink;
      
      const range = document.createRange();
      range.selectNodeContents(hoveredLink);
      savedRangeRef.current = range;
      
      setLinkUrl(url);
      setLinkText(text);
      setLinkModalOpen(true);
    }
    setHoveredLink(null);
    setLinkMenuPosition(null);
  };

  return (
    <div className={`rich-text-editor ${className}`} style={{ height: `${height}px`, position: 'relative' }}>
      <div className="rich-text-editor-toolbar">
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Bold"
          onClick={handleBold}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Italic"
          onClick={handleItalic}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Underline"
          onClick={handleUnderline}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Bullet List"
          onClick={handleBulletList}
        >
          • •
        </button>
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Numbered List"
          onClick={handleNumberedList}
        >
          1.
        </button>
        <button
          type="button"
          className="rich-text-toolbar-btn"
          title="Link"
          onClick={handleLink}
        >
          <LinkIcon size={16} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-editor-content"
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={onBlur}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        style={{ height: `calc(${height}px - 48px)`, minHeight: `calc(${height}px - 48px)` }}
      />
      
      {hoveredLink && linkMenuPosition && (
        <div
          ref={linkMenuRef}
          className="link-hover-menu"
          style={{
            top: `${linkMenuPosition.top}px`,
            left: `${linkMenuPosition.left}px`,
            position: 'absolute',
          }}
        >
          <button
            type="button"
            className="link-hover-menu-item"
            onClick={handleOpenLink}
            title="Open link"
          >
            <ExternalLink size={16} />
          </button>
          <button
            type="button"
            className="link-hover-menu-item"
            onClick={handleEditLink}
            title="Edit link"
          >
            <Edit2 size={16} />
          </button>
          <button
            type="button"
            className="link-hover-menu-item link-hover-menu-item--danger"
            onClick={handleRemoveLink}
            title="Remove link"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
      
      {linkModalOpen && (
        <div className="link-modal-overlay" onClick={handleLinkCancel}>
          <div className="link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="link-modal-header">
              <h3 className="link-modal-title">Insert Link</h3>
              <button
                type="button"
                className="link-modal-close"
                onClick={handleLinkCancel}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="link-modal-body">
              <div className="link-modal-field">
                <label className="link-modal-label">Link Text (Alias)</label>
                <input
                  type="text"
                  className="link-modal-input"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Enter link text..."
                  autoFocus
                />
              </div>
              <div className="link-modal-field">
                <label className="link-modal-label">URL</label>
                <input
                  type="text"
                  className="link-modal-input"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLinkSubmit();
                    } else if (e.key === 'Escape') {
                      handleLinkCancel();
                    }
                  }}
                />
              </div>
            </div>
            <div className="link-modal-footer">
              <button
                type="button"
                className="link-modal-btn link-modal-btn--cancel"
                onClick={handleLinkCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="link-modal-btn link-modal-btn--submit"
                onClick={handleLinkSubmit}
                disabled={!linkUrl.trim()}
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
